import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Badge,
  useTheme,
  alpha,
  Zoom
} from '@mui/material';
import {
  Home as HomeIcon,
  Category as CategoryIcon,
  ShoppingCart as ShoppingCartIcon,
  Favorite as FavoriteIcon,
  Person as PersonIcon,
  GridView as GridViewIcon,
  Store as StoreIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useCart } from '../../contexts/CartContext';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';

const MobileBottomNavigation: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { cart } = useCart();
  const { settings } = useStoreSettings();
  const getTotalItems = () => cart.totalItems;
  const [value, setValue] = useState(0);

  const isEnabled = settings?.display?.mobileBottomBarEnabled !== false;
  const showLabels = settings?.display?.mobileBottomBarShowLabels !== false;
  const isFloating = settings?.display?.mobileBottomBarStyle === 'floating';

  const defaultBottomBarItems = [
    { id: 'home', label: 'الرئيسية', icon: 'home', link: '/', enabled: true },
    { id: 'categories', label: 'الأقسام', icon: 'category', link: '/categories', enabled: true },
    { id: 'cart', label: 'السلة', icon: 'shopping_cart', link: '/cart', enabled: true },
    { id: 'wishlist', label: 'المفضلة', icon: 'favorite', link: '/favorites', enabled: true },
    { id: 'profile', label: 'حسابي', icon: 'person', link: '/account', enabled: true }
  ];

  const items = settings?.display?.mobileBottomBarItems || defaultBottomBarItems;
  const activeItems = items.filter(item => item.enabled !== false);

  const getIcon = (iconName: string) => {
    const sizeStyle = { fontSize: '1.3rem' };
    let iconElement = <HomeIcon sx={sizeStyle} />;

    switch (iconName) {
      case 'home':
        iconElement = <HomeIcon sx={sizeStyle} />;
        break;
      case 'category':
      case 'grid_view':
      case 'categories':
        iconElement = <CategoryIcon sx={sizeStyle} />;
        break;
      case 'shopping_cart':
      case 'cart':
        iconElement = <ShoppingCartIcon sx={sizeStyle} />;
        break;
      case 'favorite':
      case 'wishlist':
      case 'favorites':
        iconElement = <FavoriteIcon sx={sizeStyle} />;
        break;
      case 'person':
      case 'profile':
      case 'account':
        iconElement = <PersonIcon sx={sizeStyle} />;
        break;
      case 'store':
        iconElement = <StoreIcon sx={sizeStyle} />;
        break;
      case 'search':
        iconElement = <SearchIcon sx={sizeStyle} />;
        break;
      case 'notifications':
        iconElement = <NotificationsIcon sx={sizeStyle} />;
        break;
      case 'settings':
        iconElement = <SettingsIcon sx={sizeStyle} />;
        break;
      default:
        iconElement = <HomeIcon sx={sizeStyle} />;
    }

    if (iconName === 'shopping_cart' || iconName === 'cart') {
      return (
        <Badge badgeContent={getTotalItems()} color="error" variant="dot">
          {iconElement}
        </Badge>
      );
    }
    return iconElement;
  };

  useEffect(() => {
    const path = location.pathname;
    const index = activeItems.findIndex(item => {
      if (item.link === '/' || item.link === '/store') {
        return path === '/' || path === '/store';
      }
      return path.startsWith(item.link);
    });
    if (index !== -1) {
      setValue(index);
    }
  }, [location.pathname, activeItems]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    const link = activeItems[newValue]?.link;
    if (link) {
      navigate(link);
    }
  };

  if (!isEnabled || activeItems.length === 0) {
    return null;
  }

  return (
    <Zoom in timeout={300}>
      <Paper
        className="store-footer"
        sx={isFloating ? {
          position: 'fixed',
          bottom: 16,
          left: 16,
          right: 16,
          zIndex: 1000,
          borderRadius: '24px',
          overflow: 'hidden',
          boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.15)}`,
          background: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        } : {
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          overflow: 'hidden',
          boxShadow: `0 -4px 20px ${alpha(theme.palette.common.black, 0.1)}`,
          background: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
        elevation={0}
      >
        <BottomNavigation
          value={value}
          onChange={handleChange}
          showLabels={showLabels}
          sx={{
            bgcolor: 'transparent',
            height: 64,
            '& .MuiBottomNavigationAction-root': {
              minWidth: 'auto',
              padding: '6px 12px 8px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              borderRadius: 2,
              margin: '4px 2px',
              '&.Mui-selected': {
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                transform: 'scale(1.05)',
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }
              },
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                transform: 'scale(1.02)'
              }
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.7rem',
              fontWeight: 500,
              transition: 'all 0.3s ease'
            },
            '& .MuiSvgIcon-root': {
              fontSize: '1.3rem',
              transition: 'all 0.3s ease'
            }
          }}
        >
          {activeItems.map((item, index) => (
            <BottomNavigationAction
              key={index}
              label={item.label}
              icon={getIcon(item.icon)}
              sx={{
                '&.Mui-selected .MuiSvgIcon-root': {
                  transform: 'scale(1.1)',
                  filter: `drop-shadow(0 2px 4px ${alpha(theme.palette.primary.main, 0.3)})`
                }
              }}
            />
          ))}
        </BottomNavigation>
      </Paper>
    </Zoom>
  );
};

export default MobileBottomNavigation;
